import { Eye, List } from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useBundleTreeStore, useIsPreviewOutdated } from '@/components/bundle/bundleTreeStore';
import { type SidePanelTab, useSidePanel } from '@/components/contexts/SidePanelContext';
import Loading from '@/components/shared/Loading';
import { backendClient } from '@/lib/api-client';

const PDFViewer = lazy(() => import('@/components/pdf/PDFViewer'));

type PreviewStatus = 'idle' | 'generating' | 'ready' | 'error';

const POLL_INTERVAL_MS = 1000;
const TAB_ID = 'preview';

/**
 * Manages the bundle preview lifecycle for both full and index-only previews.
 *
 * - Triggers preview generation via the backend and polls the Temporal job until ready
 * - Caches the signed URL so repeated clicks don't regenerate
 * - Reads `previewOutdated` from the bundle tree store (compares current full-editor
 *   fingerprint against the fingerprint snapshotted on the last preview generation)
 * - Uses a single "Preview" tab in the side panel, switching between modes
 */
export function useBundlePreviewPanel(slug: string, bundleId: string) {
  // --- Side panel integration ---
  const { isOpen, tabs, openPanel, setTabs, setActiveTab } = useSidePanel();
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // --- Generation & polling ---
  const [status, setStatus] = useState<PreviewStatus>('idle');
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [indexOnly, setIndexOnly] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef(false);

  const clearPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => { abortRef.current = true; clearPolling(); };
  }, [clearPolling]);

  const generatePreview = useCallback(async (forIndexOnly: boolean) => {
    abortRef.current = false;
    clearPolling();
    setIndexOnly(forIndexOnly);
    setSignedUrl(null);
    setErrorMessage(null);
    setStatus('generating');

    // Open the side panel imperatively so the status-change effect below only
    // needs to update the existing tab. Auto-opening from that effect causes
    // the panel to immediately re-open when the user clicks the close button.
    const loadingMessage = forIndexOnly ? 'Generating index preview…' : 'Generating preview…';
    const loadingTab: SidePanelTab = {
      id: TAB_ID,
      label: forIndexOnly ? 'Preview Index' : 'Preview',
      icon: forIndexOnly ? List : Eye,
      content: (
        <div
          data-testid="bundle-preview-panel"
          className="flex flex-col items-center justify-center h-full gap-3 text-gray-500"
        >
          <Loading />
          <span className="text-sm">{loadingMessage}</span>
        </div>
      ),
    };
    if (!isOpen) {
      openPanel([loadingTab], TAB_ID);
    } else {
      const otherTabs = tabsRef.current.filter(t => t.id !== TAB_ID);
      setTabs([...otherTabs, loadingTab]);
      setActiveTab(TAB_ID);
    }

    try {
      const postUrl = `/bundle/matter/${slug}/${bundleId}/preview${forIndexOnly ? '?index_only=true' : ''}`;
      const response = await backendClient.post(postUrl);
      const { signed_url, preview_id } = response.data as { signed_url: string; preview_id: string };
      setSignedUrl(signed_url);

      const poll = async () => {
        if (abortRef.current) { clearPolling(); return; }
        try {
          const res = await backendClient.get(
            `/bundle/matter/${slug}/${bundleId}/preview/${preview_id}`,
          );
          const { status: jobStatus, error } = res.data as { status: string; error?: string };
          if (jobStatus === 'completed') {
            clearPolling();
            setSignedUrl(signed_url);
            setStatus('ready');
            return;
          }
          if (jobStatus === 'failed') {
            clearPolling();
            setStatus('error');
            setErrorMessage(error || 'Preview failed');
            return;
          }
        } catch (err: any) {
          const statusCode = err?.response?.status;
          if (statusCode === 404) {
            clearPolling();
            setStatus('error');
            setErrorMessage(err?.response?.data?.detail || 'Preview job not found');
            return;
          }
          if (typeof statusCode === 'number' && statusCode >= 400 && statusCode < 500) {
            clearPolling();
            setStatus('error');
            setErrorMessage(err?.response?.data?.detail || 'Preview failed');
            return;
          }
          // Network / 5xx — keep waiting on Temporal
        }
        if (abortRef.current) { clearPolling(); return; }
        pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      };
      pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    } catch (err: any) {
      setStatus('error');
      setSignedUrl(null);
      setErrorMessage(err?.response?.data?.detail || err?.message || 'Failed to start preview');
    }
  }, [slug, bundleId, clearPolling, isOpen, openPanel, setTabs, setActiveTab]);

  // --- Caching & outdated tracking ---
  const [cachedPreviewUrl, setCachedPreviewUrl] = useState<string | null>(null);
  const previewOutdated = useIsPreviewOutdated();
  const previewedFingerprint = useBundleTreeStore(s => s.previewedFingerprint);

  useEffect(() => {
    if (status === 'ready' && signedUrl) {
      setCachedPreviewUrl(signedUrl);
      useBundleTreeStore.getState().actions.markPreviewed();
    }
  }, [status, signedUrl]);

  // Drop the local cached URL when the store resets `previewedFingerprint` to null
  // (e.g. after save → initialize). Otherwise the next click hits the cache branch
  // with a stale signed URL pointing at the pre-save PDF.
  useEffect(() => {
    if (previewedFingerprint === null) {
      setCachedPreviewUrl(null);
      setStatus('idle');
    }
  }, [previewedFingerprint]);

  // --- Side panel tab ---
  const buildPreviewTab = useCallback((): SidePanelTab => {
    const tabLabel = indexOnly ? 'Preview Index' : 'Preview';
    const TabIcon = indexOnly ? List : Eye;

    if (status === 'generating') {
      const loadingMessage = indexOnly ? 'Generating index preview\u2026' : 'Generating preview\u2026';
      return {
        id: TAB_ID,
        label: tabLabel,
        icon: TabIcon,
        content: (
          <div
            data-testid="bundle-preview-panel"
            className="flex flex-col items-center justify-center h-full gap-3 text-gray-500"
          >
            <Loading />
            <span className="text-sm">{loadingMessage}</span>
          </div>
        ),
      };
    }
    if (status === 'error') {
      return {
        id: TAB_ID,
        label: tabLabel,
        icon: TabIcon,
        content: (
          <div
            data-testid="bundle-preview-panel"
            className="flex flex-col items-center justify-center h-full gap-2 text-gray-500 px-4 text-center"
          >
            <p className="text-sm font-medium text-red-600">Preview failed</p>
            <p className="text-xs">{errorMessage || 'An unexpected error occurred'}</p>
          </div>
        ),
      };
    }
    const url = cachedPreviewUrl || signedUrl;
    return {
      id: TAB_ID,
      label: tabLabel,
      icon: TabIcon,
      content: url ? (
        <div data-testid="bundle-preview-panel" className="flex flex-col h-full">
          {previewOutdated && (
            <div
              data-testid="preview-outdated-indicator"
              className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-amber-700 text-xs flex items-center gap-2"
            >
              <span>Preview is outdated — the bundle structure has changed since this was generated.</span>
            </div>
          )}
          <div data-testid="bundle-preview-pdf" className="flex-1 min-h-0">
            <Suspense fallback={<div className="flex items-center justify-center h-full"><Loading /></div>}>
              <PDFViewer
                fileUrl={url}
                highlightGroups={[]}
                slug={slug}
                viewMode="sidebar"
              />
            </Suspense>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full text-gray-400 text-sm">
          Click Preview to generate
        </div>
      ),
    };
  }, [status, signedUrl, cachedPreviewUrl, errorMessage, previewOutdated, slug, indexOnly]);

  // Keep the preview tab content in sync as state changes. Opening the panel
  // and choosing the active tab are handled imperatively in generatePreview /
  // showCachedOrGenerate — doing either here would steal focus when the panel
  // opens for an unrelated reason (e.g. the user clicked a document to view).
  useEffect(() => {
    if (!isOpen) return;
    if (status === 'idle' && !cachedPreviewUrl) return;

    const previewTab = buildPreviewTab();
    const otherTabs = tabsRef.current.filter(t => t.id !== TAB_ID);
    setTabs([...otherTabs, previewTab]);
  }, [status, cachedPreviewUrl, buildPreviewTab, isOpen, setTabs]);

  // Re-inject preview tab when other tabs replace it (e.g. opening a document)
  useEffect(() => {
    if (!cachedPreviewUrl || !isOpen) return;
    const hasPreviewTab = tabs.some(t => t.id === TAB_ID);
    if (!hasPreviewTab) {
      setTabs([...tabs, buildPreviewTab()]);
    }
  }, [tabs, isOpen, cachedPreviewUrl, buildPreviewTab, setTabs]);

  // --- Public API ---
  const showCachedOrGenerate = useCallback((forIndexOnly: boolean) => {
    if (cachedPreviewUrl && !previewOutdated && status !== 'generating' && indexOnly === forIndexOnly) {
      const previewTab = buildPreviewTab();
      if (!isOpen) {
        openPanel([previewTab], TAB_ID);
      } else {
        const otherTabs = tabsRef.current.filter(t => t.id !== TAB_ID);
        setTabs([...otherTabs, previewTab]);
        setActiveTab(TAB_ID);
      }
      return;
    }
    generatePreview(forIndexOnly);
  }, [cachedPreviewUrl, previewOutdated, status, indexOnly, generatePreview, isOpen, buildPreviewTab, openPanel, setTabs, setActiveTab]);

  const handlePreviewClick = useCallback(() => showCachedOrGenerate(false), [showCachedOrGenerate]);
  const handlePreviewIndexClick = useCallback(() => showCachedOrGenerate(true), [showCachedOrGenerate]);

  return {
    handlePreviewClick,
    previewLoading: status === 'generating' && !indexOnly,
    handlePreviewIndexClick,
    previewIndexLoading: status === 'generating' && indexOnly,
  };
}
